export const analyzeSingleProfileMedication = (profile, medication) => {
    const profileLower = profile.toLowerCase();
    let isContraindicated = false;
    const details = [];
    const warnings = [];

    if (medication.profilesContreIndiquees) {
        const contraindicatedProfiles = parseContraindicatedProfiles(medication.profilesContreIndiquees);
        const isProfileContraindicated = contraindicatedProfiles.some(p => 
            p.toLowerCase().includes(profileLower) || profileLower.includes(p.toLowerCase())
        );
        
        if (isProfileContraindicated) {
            isContraindicated = true;
            details.push('Profil explicitement listé dans les contre-indications');
        }
    }

    if (medication.contreIndications) {
        const contraindicationsText = medication.contreIndications.toLowerCase();
        if (contraindicationsText.includes(profileLower)) {
            isContraindicated = true;
            details.push('Profil mentionné dans les contre-indications générales');
        }
    }

    if (medication.precautionsEmploi) {
        const precautionsText = medication.precautionsEmploi.toLowerCase();
        if (precautionsText.includes(profileLower)) {
            warnings.push('Précautions d\'emploi requises pour ce profil');
        }
    }


    return {
        isContraindicated,
        details,
        warnings,
        severity: isContraindicated ? 'high' : warnings.length > 0 ? 'medium' : 'low'
    };
};

export const parseContraindicatedProfiles = (profilesContreIndiquees) => {
    if (!profilesContreIndiquees || typeof profilesContreIndiquees !== 'string') {
        return [];
    }

    if (profilesContreIndiquees.includes('$')) {
        return profilesContreIndiquees.split('$')
            .map(p => p.trim())
            .filter(p => p.length > 0);
    }

    let cleanProfiles = '';
    let insideParentheses = false;

    for (const char of profilesContreIndiquees) {
        if (char === '(') {
            insideParentheses = true;
            cleanProfiles += char;
        } else if (char === ')') {
            insideParentheses = false;
            cleanProfiles += char;
        } else if (char === ',' && !insideParentheses) {
            cleanProfiles += '$';
        } else {
            cleanProfiles += char;
        }
    }

    return cleanProfiles.split('$')
        .map(p => p.trim())
        .filter(p => p.length > 0);
};

export const findTherapeuticAlternatives = (contraindicatedMedication, profile, allMedications, options = {}) => {
    const {
        maxAlternatives = 10,
        strictFamilyMatch = false,
        includeSameSubstance = false
    } = options;

    const alternatives = [];

    const searchCriteria = {
        famille: contraindicatedMedication.famille || contraindicatedMedication.familleTherapeutique,
        classe: contraindicatedMedication.classe || contraindicatedMedication.classePharmacologique,
        dci: contraindicatedMedication.dci || contraindicatedMedication.substance,
        indication: contraindicatedMedication.indication
    };

    for (const medication of allMedications) {
        if (medication._id === contraindicatedMedication._id ||
            medication.id === contraindicatedMedication.id ||
            medication.nom === contraindicatedMedication.nom) {
            continue;
        }

        const matchesCriteria = checkAlternativeMatch(medication, searchCriteria, {
            strictFamilyMatch,
            includeSameSubstance
        });

        if (matchesCriteria) {
            const analysis = analyzeSingleProfileMedication(profile, medication);

            if (!analysis.isContraindicated) {
                alternatives.push({
                    id: medication._id || medication.id,
                    nom: medication.nom || medication.name,
                    dci: medication.dci || medication.substance,
                    famille: medication.famille || medication.familleTherapeutique,
                    classe: medication.classe || medication.classePharmacologique,
                    laboratoire: medication.laboratoire || medication.laboratory,
                    forme: medication.forme,
                    dosage: medication.dosage,
                    indication: medication.indication,
                    warnings: analysis.warnings,
                    details: analysis.details,
                    severity: analysis.severity,
                    matchScore: calculateMatchScore(medication, searchCriteria)
                });
            }
        }

        if (alternatives.length >= maxAlternatives) {
            break;
        }
    }

    return alternatives.sort((a, b) => b.matchScore - a.matchScore);
};

const checkAlternativeMatch = (medication, searchCriteria, options) => {
    const { strictFamilyMatch, includeSameSubstance } = options;

    const familyMatch = searchCriteria.famille &&
        (medication.famille === searchCriteria.famille || 
         medication.familleTherapeutique === searchCriteria.famille ||
         (medication.famille && medication.famille.toLowerCase().includes(searchCriteria.famille.toLowerCase())) ||
         (medication.familleTherapeutique && medication.familleTherapeutique.toLowerCase().includes(searchCriteria.famille.toLowerCase())));

    const classMatch = searchCriteria.classe &&
        (medication.classe === searchCriteria.classe || 
         medication.classePharmacologique === searchCriteria.classe ||
         (medication.classe && medication.classe.toLowerCase().includes(searchCriteria.classe.toLowerCase())) ||
         (medication.classePharmacologique && medication.classePharmacologique.toLowerCase().includes(searchCriteria.classe.toLowerCase())));

    const substanceMatch = !includeSameSubstance ?
        (medication.dci !== searchCriteria.dci && medication.substance !== searchCriteria.dci) : true;

    if (strictFamilyMatch) {
        return familyMatch && substanceMatch;
    }

    return (familyMatch || classMatch) && substanceMatch;
};

const calculateMatchScore = (medication, searchCriteria) => {
    let score = 0;

    if (medication.famille === searchCriteria.famille ||
        medication.familleTherapeutique === searchCriteria.famille) {
        score += 40;
    } else if (searchCriteria.famille && 
               ((medication.famille && medication.famille.toLowerCase().includes(searchCriteria.famille.toLowerCase())) ||
                (medication.familleTherapeutique && medication.familleTherapeutique.toLowerCase().includes(searchCriteria.famille.toLowerCase())))) {
        score += 20;
    }

    if (medication.classe === searchCriteria.classe ||
        medication.classePharmacologique === searchCriteria.classe) {
        score += 30;
    } else if (searchCriteria.classe && 
               ((medication.classe && medication.classe.toLowerCase().includes(searchCriteria.classe.toLowerCase())) ||
                (medication.classePharmacologique && medication.classePharmacologique.toLowerCase().includes(searchCriteria.classe.toLowerCase())))) {
        score += 15;
    }

    if (medication.indication === searchCriteria.indication) {
        score += 20;
    } else if (searchCriteria.indication && medication.indication &&
               medication.indication.toLowerCase().includes(searchCriteria.indication.toLowerCase())) {
        score += 10;
    }

    if (medication.forme) {
        score += 10;
    }

    return score;
};


export const generateAnalysisReport = (profiles, medications, analysisResult) => {
    const timestamp = new Date().toISOString();
    const totalCombinations = profiles.length * medications.length;
    const contraindicatedCombinations = analysisResult.analyse.filter(
        item => item.resultat === 'contre-indiqué'
    ).length;

    return {
        metadata: {
            timestamp,
            profilesCount: profiles.length,
            medicationsCount: medications.length,
            totalCombinations,
            contraindicatedCombinations,
            contraindicationRate: (contraindicatedCombinations / totalCombinations * 100).toFixed(2) + '%'
        },
        summary: {
            profilesAnalyzed: profiles,
            medicationsAnalyzed: medications.map(med => ({
                id: med,
            })),
            riskLevel: contraindicatedCombinations === 0 ? 'low' : 
                      contraindicatedCombinations <= totalCombinations * 0.3 ? 'medium' : 'high'
        },
        results: analysisResult,
        recommendations: generateRecommendations(analysisResult)
    };
};

const generateRecommendations = (analysisResult) => {
    const recommendations = [];
    const contraindicatedItems = analysisResult.analyse.filter(
        item => item.resultat === 'contre-indiqué'
    );

    if (contraindicatedItems.length === 0) {
        recommendations.push({
            type: 'success',
            message: 'Aucune contre-indication détectée pour les combinaisons analysées.',
            priority: 'low'
        });
    } else {
        recommendations.push({
            type: 'warning',
            message: `${contraindicatedItems.length} contre-indication(s) détectée(s). Consultez les alternatives proposées.`,
            priority: 'high'
        });

        contraindicatedItems.forEach(item => {
            recommendations.push({
                type: 'info',
                message: `Pour ${item.medicament} (contre-indiqué avec ${item.profil}), considérez les alternatives de même famille thérapeutique.`,
                priority: 'medium',
                medication: item.medicament,
                profile: item.profil
            });
        });
    }

    return recommendations;
};

const multiparametricAnalysisUtils = {
    analyzeSingleProfileMedication,
    parseContraindicatedProfiles,
    findTherapeuticAlternatives,
    generateAnalysisReport
};

export default multiparametricAnalysisUtils;

